import { Link } from 'react-router-dom'
import {
  Database, ShieldCheck, Search, Calculator, Star, BookOpen,
  ArrowRight, Building2, CheckCircle2,
} from 'lucide-react'
import SEO from '@/components/common/SEO'

/**
 * 회사소개(About) — 운영 주체·서비스 목적·데이터 출처·신뢰성.
 * 애드센스 신뢰 페이지 요건 + SEO 고유 콘텐츠. petpro.me 레이아웃 완성도 참고(브랜드 블루 유지).
 * 영구지침: docs/plan/2026-06-08-adsense-content-strategy.md §5 Phase A
 */
export default function AboutPage() {
  return (
    <>
      <SEO
        title="회사소개"
        description="케어밸런스는 국민건강보험공단·건강보험심사평가원 공공데이터를 기반으로 전국 3만여 개 요양시설을 비용·평가등급·후기로 투명하게 비교하는 서비스입니다."
        url="/about"
      />

      {/* ── Hero ─────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#1e3a8a] via-[#2563eb] to-[#4338ca]">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '22px 22px' }}
        />
        <div className="relative mx-auto max-w-5xl px-6 py-20 text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3.5 py-1.5 text-xs font-semibold text-white backdrop-blur-sm">
            <Building2 size={13} /> 케어밸런스 소개
          </span>
          <h1 className="mt-5 text-3xl font-bold leading-tight text-white sm:text-4xl">
            요양시설 선택,<br className="sm:hidden" /> 데이터로 투명하게
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-white/80">
            부모님을 모실 요양시설을 고르는 일은 가족에게 가장 어려운 결정입니다.
            케어밸런스는 전국 요양시설의 공공데이터를 한곳에 모아,
            비용·품질·후기를 한눈에 비교하고 합리적으로 선택하도록 돕습니다.
          </p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            <Link to="/facilities" className="inline-flex items-center gap-1.5 rounded-xl bg-white px-6 py-3 text-sm font-bold text-[#2563eb] shadow-sm hover:bg-cb-gray-50">
              시설 검색하기 <ArrowRight size={16} />
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-1.5 rounded-xl border border-white/40 px-6 py-3 text-sm font-bold text-white hover:bg-white/10">
              문의하기
            </Link>
          </div>
        </div>
      </section>

      {/* ── 통계 밴드 ────────────────────────────────────── */}
      <section className="relative z-10 mx-auto mt-6 max-w-5xl px-6 sm:-mt-10">
        <div className="grid grid-cols-3 gap-3 rounded-2xl border border-cb-gray-100 bg-white p-6 shadow-md sm:gap-6 sm:p-8">
          <Stat value="3만+" label="전국 요양시설" />
          <Stat value="3개 기관" label="공공데이터 연계" />
          <Stat value="무료" label="검색·비용계산·상담" />
        </div>
      </section>

      {/* ── 제공 서비스 ──────────────────────────────────── */}
      <section className="mx-auto max-w-5xl px-6 py-16">
        <div className="mb-8 text-center">
          <h2 className="text-2xl font-bold text-cb-gray-900">케어밸런스가 제공하는 것</h2>
          <p className="mt-2 text-body-sm text-cb-gray-500">보호자가 시설을 비교·선택하는 모든 과정을 돕습니다</p>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Feature icon={<Search size={20} />} title="시설 비교 검색" desc="지역·시설유형·평가등급·비용으로 요양원, 요양병원, 주간보호센터, 방문요양을 한눈에 비교합니다." />
          <Feature icon={<Calculator size={20} />} title="비용 시뮬레이터" desc="장기요양등급, 시설 규모, 감경 여부에 따른 월 예상 본인부담금과 비급여 비용을 계산합니다." />
          <Feature icon={<Star size={20} />} title="인증 후기와 상담" desc="실제 보호자 후기를 확인하고, 시설에 직접 입소 상담을 무료로 요청할 수 있습니다." />
          <Feature icon={<BookOpen size={20} />} title="돌봄 정보 가이드" desc="장기요양등급 신청, 시설 종류와 차이, 비용 구조 등 보호자에게 필요한 정보를 정리해 제공합니다." />
        </div>
      </section>

      {/* ── 데이터 출처 / 신뢰성 ─────────────────────────── */}
      <section className="bg-cb-gray-50 py-16">
        <div className="mx-auto max-w-5xl px-6">
          <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-2">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-cb-primary/10 px-3 py-1.5 text-xs font-semibold text-cb-primary">
                <ShieldCheck size={14} /> 신뢰할 수 있는 데이터
              </div>
              <h2 className="mt-4 text-2xl font-bold text-cb-gray-900">공식 공공데이터를 기반으로 합니다</h2>
              <p className="mt-3 text-body-sm leading-relaxed text-cb-gray-600">
                시설 기본 정보(주소, 정원·현원, 인력 현황, 평가등급 등)는 아래 공공기관의
                공식 데이터를 기반으로 하며, 매일·매주 자동으로 갱신됩니다.
              </p>
              <p className="mt-4 rounded-xl bg-amber-50 p-4 text-caption leading-relaxed text-amber-800">
                ※ 공공데이터는 갱신 시점에 따라 실제와 차이가 있을 수 있으며, 시설운영자가 직접 등록한
                정보의 정확성은 해당 시설에 책임이 있습니다. 입소 결정 전 시설에 직접 확인하시기 바랍니다.
              </p>
            </div>
            <div className="space-y-3">
              <Source icon={<Database size={18} />} org="국민건강보험공단" desc="장기요양기관 기본정보 — 정원, 인력, 운영 현황, 급여종류별 평가등급" />
              <Source icon={<Database size={18} />} org="건강보험심사평가원" desc="요양병원 정보 — 진료시간, 진료과목, 병상, 주차" />
              <Source icon={<Database size={18} />} org="보건복지부" desc="장기요양 급여 수가 및 본인부담 정책" />
            </div>
          </div>
        </div>
      </section>

      {/* ── 운영 주체 ────────────────────────────────────── */}
      <section className="mx-auto max-w-5xl px-6 py-16">
        <div className="mb-6 text-center">
          <h2 className="text-2xl font-bold text-cb-gray-900">운영 주체</h2>
        </div>
        <div className="mx-auto max-w-2xl rounded-2xl border border-cb-gray-200 bg-white p-7 shadow-sm">
          <dl className="grid grid-cols-1 gap-y-3 text-body-sm sm:grid-cols-[140px_1fr]">
            <dt className="font-semibold text-cb-gray-900">상호</dt><dd className="text-cb-gray-700">파인드플레이스 (CareBalance)</dd>
            <dt className="font-semibold text-cb-gray-900">대표</dt><dd className="text-cb-gray-700">우상철</dd>
            <dt className="font-semibold text-cb-gray-900">사업자등록번호</dt><dd className="text-cb-gray-700">587-18-02704</dd>
            <dt className="font-semibold text-cb-gray-900">주소</dt><dd className="text-cb-gray-700">경기도 의정부시 서부로 545 경민대학교 창업관 7층 754-1호 파인드플레이스</dd>
            <dt className="font-semibold text-cb-gray-900">이메일</dt>
            <dd><a href="mailto:contact@carebalance.co.kr" className="text-cb-primary hover:underline">contact@carebalance.co.kr</a></dd>
          </dl>
          <div className="mt-6 flex flex-wrap gap-3 border-t border-cb-gray-100 pt-5 text-body-sm">
            <Link to="/contact" className="inline-flex items-center gap-1.5 font-semibold text-cb-primary hover:underline">
              <CheckCircle2 size={15} /> 연락처·문의
            </Link>
            <Link to="/terms/privacy-policy" className="inline-flex items-center gap-1.5 font-semibold text-cb-primary hover:underline">
              <CheckCircle2 size={15} /> 개인정보 처리방침
            </Link>
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────── */}
      <section className="mx-auto max-w-5xl px-6 pb-16">
        <div className="rounded-2xl bg-gradient-to-r from-[#1e3a8a] to-[#2563eb] px-8 py-10 text-center">
          <h2 className="text-xl font-bold text-white sm:text-2xl">지금 우리 동네 요양시설을 찾아보세요</h2>
          <p className="mt-2 text-sm text-white/70">3만여 개 시설 · 무료 상담 · AI 맞춤 추천</p>
          <Link to="/facilities" className="mt-6 inline-flex items-center gap-1.5 rounded-xl bg-white px-7 py-3 text-sm font-bold text-[#2563eb] hover:bg-cb-gray-50">
            시설 검색하기 <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </>
  )
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div className="text-center">
      <div className="text-xl font-bold text-cb-primary sm:text-2xl">{value}</div>
      <div className="mt-1 text-caption text-cb-gray-500 sm:text-body-sm">{label}</div>
    </div>
  )
}

function Feature({ icon, title, desc }: { icon: React.ReactNode; title: string; desc: string }) {
  return (
    <div className="rounded-2xl border border-cb-gray-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md">
      <div className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-cb-primary/10 text-cb-primary">
        {icon}
      </div>
      <h3 className="font-bold text-cb-gray-900">{title}</h3>
      <p className="mt-1.5 text-body-sm leading-relaxed text-cb-gray-600">{desc}</p>
    </div>
  )
}

function Source({ icon, org, desc }: { icon: React.ReactNode; org: string; desc: string }) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-cb-gray-200 bg-white p-4">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-cb-primary/10 text-cb-primary">{icon}</div>
      <div>
        <p className="font-bold text-cb-gray-900">{org}</p>
        <p className="text-caption leading-relaxed text-cb-gray-500">{desc}</p>
      </div>
    </div>
  )
}
